import BigNumber from 'bignumber.js'

import {
  asString,
  compareBigNumbers,
  compareNumbers,
  compareTimestamps,
  comparingNullable,
  comparingString,
  type RowComparator,
  type SortDirection,
} from '../compare'
import {
  cell,
  isEmptyByDefault,
  isEqualByDefault,
  toHumanReadableByDefault,
  userSortName,
  type ContainsKind,
  type FieldBehavior,
  type LinkedRowValue,
  type SelectOptionValue,
  type FieldContext,
  type UserValue,
} from './behavior'
import {
  FORMULA_TYPE_BEHAVES_AS,
  isArrayFormulaType,
  unwrapArrayItem,
  type FormulaType,
} from '../formula-types'
import { parseDurationValue, formatDurationValue } from '../duration'
import { formatDateValue, type DateFieldLike } from '../dates'
import { formatDecimalNumber } from '../numbers'
import { parseBooleanValue } from '../booleans'
import type {
  Field,
  FieldKey,
  FieldOf,
  FieldTypeName,
  LinkRowPrimaryField,
  RowData,
} from '@/api/types'

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

/** A type that answers everything the same way as plain text. */
function textLike<T extends FieldTypeName>(type: T): FieldBehavior<T> {
  return {
    type,
    isEmpty: (_field, value) => isEmptyByDefault(value),
    isEqual: (_field, a, b) => isEqualByDefault(a, b),
    getSort: (_field, key, direction) => comparingString(key, direction, asString),
    containsKind: () => 'text' as const,
    containsWordKind: () => 'text' as const,
    toHumanReadable: (_field, value) => toHumanReadableByDefault(value),
    parseFromLinkedRowItemValue: (_field, value) => value,
  }
}

/** A type that has no order of its own, so a view cannot be sorted by it. */
function unsortable<T extends FieldTypeName>(
  type: T,
  toHumanReadable: (value: unknown, delimiter: string) => string = (value) =>
    toHumanReadableByDefault(value),
  containsKind: ContainsKind = 'none',
): FieldBehavior<T> {
  return {
    type,
    isEmpty: (_field, value) => isEmptyByDefault(value),
    isEqual: (_field, a, b) => isEqualByDefault(a, b),
    getSort: () => null,
    containsKind: () => containsKind,
    containsWordKind: () => 'none' as const,
    toHumanReadable: (_field, value, _context, delimiter = ', ') =>
      toHumanReadable(value, delimiter),
    parseFromLinkedRowItemValue: (_field, value) => value,
  }
}

/** A timestamp, whether the user set it or the backend did. */
function dateLike<T extends FieldTypeName>(type: T): FieldBehavior<T> {
  return {
    type,
    isEmpty: (_field, value) => isEmptyByDefault(value),
    isEqual: (_field, a, b) => isEqualByDefault(a, b),
    getSort: (_field, key, direction) =>
      comparingNullable(key, direction, compareTimestamps),
    // `FieldOf<T>` is generic here, so the compiler cannot see the date
    // properties even though every type this builder is used for has them.
    containsKind: () => 'text' as const,
    containsWordKind: () => 'none' as const,
    toHumanReadable: (field, value) => formatDateValue(field as DateFieldLike, value),
    parseFromLinkedRowItemValue: (_field, value) => value,
  }
}

/** A user reference, sorted by the name the workspace currently gives them. */
function userLike<T extends FieldTypeName>(type: T): FieldBehavior<T> {
  return {
    type,
    isEmpty: (_field, value) => isEmptyByDefault(value),
    isEqual: (_field, a, b) => isEqualByDefault(a, b),
    getSort: (_field, key, direction, context) =>
      comparingString(key, direction, (value) => userSortName(value, context)),
    // The workspace's current name for them, not the one stored on the row --
    // otherwise a rename leaves filters matching a name nobody sees any more.
    containsKind: () => 'text' as const,
    containsWordKind: () => 'none' as const,
    toHumanReadable: (_field, value, context) =>
      value === null || value === undefined ? '' : userSortName(value, context),
    parseFromLinkedRowItemValue: (_field, value) => value,
  }
}

const selectOptionText = (value: unknown): string =>
  value === null || value === undefined ? '' : String((value as SelectOptionValue).value)

// ---------------------------------------------------------------------------
// The map
// ---------------------------------------------------------------------------

const text = textLike('text')
const longText = textLike('long_text')

const number: FieldBehavior<'number'> = {
  type: 'number',
  isEmpty: (_field, value) => isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  // Compared as decimals rather than as doubles: the backend serialises a number
  // field as a string precisely because the value may not survive a double, and
  // reading it as one here would order two cells that differ past the sixteenth
  // digit arbitrarily.
  getSort: (_field, key, direction) =>
    comparingNullable(key, direction, compareBigNumbers),
  // Rendered with the field's own separators, prefix and suffix, so a filter
  // matching "1,234" matches what the cell actually shows.
  containsKind: () => 'text' as const,
  containsWordKind: () => 'none' as const,
  toHumanReadable: (field, value) =>
    typeof value === 'string' || typeof value === 'number'
      ? formatDecimalNumber(field, value)
      : '',
  parseFromLinkedRowItemValue: (_field, value) =>
    value === '' ? null : new BigNumber(value),
}

const rating: FieldBehavior<'rating'> = {
  type: 'rating',
  // Zero stars is an unrated row, not a rating of zero.
  isEmpty: (_field, value) => value === 0 || isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  // Never null -- an unrated row is stored as 0 -- so there is nothing to order
  // around, unlike every other numeric type here.
  getSort: (_field, key, direction) => (a, b) => {
    const result = compareNumbers(cell(a, key), cell(b, key))
    return direction === 'ASC' ? result : -result
  },
  containsKind: () => 'text' as const,
  containsWordKind: () => 'none' as const,
  toHumanReadable: (_field, value) => toHumanReadableByDefault(value),
  parseFromLinkedRowItemValue: (_field, value) => value,
}

const boolean: FieldBehavior<'boolean'> = {
  type: 'boolean',
  isEmpty: (_field, value) => isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  getSort: (_field, key, direction) => (a, b) => {
    const left = Number(cell(a, key))
    const right = Number(cell(b, key))
    return direction === 'ASC' ? left - right : right - left
  },
  // "true" / "false" -- the words, which is what a filter on a checkbox column
  // is written against. Anything that is not a boolean falls through to the
  // default, where `false` would otherwise render as empty.
  containsKind: () => 'text' as const,
  containsWordKind: () => 'none' as const,
  toHumanReadable: (_field, value) =>
    typeof value === 'boolean' ? String(value) : toHumanReadableByDefault(value),
  parseFromLinkedRowItemValue: (_field, value) => parseBooleanValue(value),
}

const duration: FieldBehavior<'duration'> = {
  type: 'duration',
  isEmpty: (_field, value) => isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  getSort: (_field, key, direction) => comparingNullable(key, direction, compareNumbers),
  containsKind: () => 'none' as const,
  containsWordKind: () => 'none' as const,
  toHumanReadable: (field, value) =>
    typeof value === 'number'
      ? formatDurationValue(value, field.duration_format ?? 'h:mm')
      : '',
  parseFromLinkedRowItemValue: (field, value) =>
    parseDurationValue(value, field.duration_format ?? 'h:mm'),
}

const singleSelect: FieldBehavior<'single_select'> = {
  type: 'single_select',
  isEmpty: (_field, value) => isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  getSort: (_field, key, direction) => comparingString(key, direction, selectOptionText),
  // Only the option's text survives into a link row cell, so the id is lost and
  // the comparison falls back to text -- which is what sorting by it does anyway.
  containsKind: () => 'text' as const,
  containsWordKind: () => 'text' as const,
  toHumanReadable: (_field, value) => selectOptionText(value),
  parseFromLinkedRowItemValue: (_field, value) => (value === '' ? null : { value }),
}

const multipleSelect: FieldBehavior<'multiple_select'> = {
  type: 'multiple_select',
  isEmpty: (_field, value) => isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  getSort: (_field, key, direction) =>
    comparingString(key, direction, (value) =>
      ((value ?? []) as SelectOptionValue[]).map((option) => option.value).join(', '),
    ),
  containsKind: () => 'text' as const,
  containsWordKind: () => 'text' as const,
  // Guarded on the shape rather than on emptiness: upstream calls `.map` on
  // whatever it is handed and raises a TypeError on a scalar, which aborts the
  // whole row-match pass rather than this one cell.
  toHumanReadable: (_field, value, _context, delimiter = ', ') =>
    Array.isArray(value)
      ? (value as SelectOptionValue[]).map((option) => option.value).join(delimiter)
      : '',
  parseFromLinkedRowItemValue: (_field, value) =>
    value.split(',').map((option) => ({ value: option.trim() })),
}

const multipleCollaborators: FieldBehavior<'multiple_collaborators'> = {
  type: 'multiple_collaborators',
  isEmpty: (_field, value) => isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  getSort: (_field, key, direction, context) =>
    comparingString(key, direction, (value) =>
      ((value ?? []) as UserValue[]).map((user) => userSortName(user, context)).join(''),
    ),
  containsKind: () => 'none' as const,
  containsWordKind: () => 'none' as const,
  toHumanReadable: (_field, value, context, delimiter = ', ') =>
    Array.isArray(value)
      ? (value as UserValue[]).map((user) => userSortName(user, context)).join(delimiter)
      : '',
  // A link row carries the collaborators' email addresses, which only the
  // workspace's member list can turn back into users -- and this layer has no
  // access to it, so the text is kept as it stands.
  parseFromLinkedRowItemValue: (_field, value) => value,
}

const autonumber: FieldBehavior<'autonumber'> = {
  type: 'autonumber',
  isEmpty: (_field, value) => isEmptyByDefault(value),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  containsKind: () => 'text' as const,
  containsWordKind: () => 'none' as const,
  toHumanReadable: (_field, value) => toHumanReadableByDefault(value),
  getSort: (_field, key, direction) =>
    comparingNullable(key, direction, compareBigNumbers),
  parseFromLinkedRowItemValue: (_field, value) => value,
}

const linkRow: FieldBehavior<'link_row'> = {
  type: 'link_row',
  // A cell holding only rows that have not been saved yet has nothing in it: the
  // default rule would see a non-empty array and call it filled.
  isEmpty: (_field, value) =>
    !Array.isArray(value) ||
    value.length === 0 ||
    (value as LinkedRowValue[]).every((item) => !Number.isInteger(item.id)),
  isEqual: (_field, a, b) => isEqualByDefault(a, b),
  containsKind: () => 'none' as const,
  containsWordKind: () => 'none' as const,
  // Only the related rows' primary-field text, joined -- never their ids, which
  // is what makes `link_row_contains` a text match rather than a lookup.
  //
  // A bare string is a name with no row behind it yet. Only this application
  // produces one, and only for a moment: text pasted into a link row column is
  // sent as names, because names are all it has, and the value shown until the
  // server answers is exactly what was pasted.
  toHumanReadable: (_field, value, _context, delimiter = ', ') =>
    Array.isArray(value)
      ? value
          .map((link) =>
            typeof link === 'string' ? link : ((link as LinkedRowValue).value ?? ''),
          )
          .join(delimiter)
      : '',
  getSort: (field, key, direction, context) =>
    sortLinkRow(field, key, direction, context),
  parseFromLinkedRowItemValue: (_field, value) => value,
}

/**
 * A formula and the three fields that are formulas with a form around them.
 *
 * Count, rollup and lookup all report a `formula_type` and behave exactly as a
 * formula does; only the configuration UI differs, and none of that is here.
 */
function formulaLike<T extends 'formula' | 'count' | 'rollup' | 'lookup'>(
  type: T,
): FieldBehavior<T> {
  return {
    type,
    isEmpty: (_field, value) => isEmptyByDefault(value),
    isEqual: (_field, a, b) => isEqualByDefault(a, b),
    containsKind: (field) => formulaDelegate(field)?.containsKind(field) ?? 'none',
    containsWordKind: (field) =>
      formulaDelegate(field)?.containsWordKind(field) ?? 'none',
    toHumanReadable: (field, value, context, delimiter) =>
      formulaToHumanReadable(field, value, context, delimiter),
    getSort: (field, key, direction, context) =>
      sortFormula(field, key, direction, context),
    parseFromLinkedRowItemValue: (field, value) => parseFormulaLinkedValue(field, value),
  }
}

/**
 * Every field type, and what the application knows about each.
 *
 * `satisfies` with a mapped type is the point of the whole file: the key set has
 * to be exactly `FieldTypeName`, so regenerating the API contract with a new
 * field type in it turns into a compile error here rather than a runtime lookup
 * that returns undefined somewhere deep in a grid.
 */
export const FIELD_BEHAVIOR = {
  text,
  long_text: longText,
  url: textLike('url'),
  email: textLike('email'),
  phone_number: textLike('phone_number'),
  uuid: textLike('uuid'),
  number,
  rating,
  boolean,
  duration,
  date: dateLike('date'),
  created_on: dateLike('created_on'),
  last_modified: dateLike('last_modified'),
  created_by: userLike('created_by'),
  last_modified_by: userLike('last_modified_by'),
  single_select: singleSelect,
  multiple_select: multipleSelect,
  multiple_collaborators: multipleCollaborators,
  autonumber,
  link_row: linkRow,
  formula: formulaLike('formula'),
  count: formulaLike('count'),
  rollup: formulaLike('rollup'),
  lookup: formulaLike('lookup'),
  // A file has no order, a password is never readable, and the form-view row
  // marker is not a real column. Each of these returns null from `getSort`, so a
  // view cannot be ordered by one at all.
  // The file names, joined. `filename_contains` deliberately does not use this
  // -- it reads the files themselves, so that the commas cannot be matched and
  // a filter value cannot span the end of one name and the start of the next.
  file: unsortable(
    'file',
    (value, delimiter) =>
      Array.isArray(value)
        ? (value as { visible_name?: string | null }[])
            .map((f) => f.visible_name ?? '')
            .join(delimiter)
        : '',
    'filename',
  ),
  // Never the password. A set one renders as a fixed run of bullets, so every
  // password looks alike and its length does not leak either.
  password: unsortable('password', (value) => (value ? '\u2022'.repeat(10) : '')),
  form_view_edit_row: unsortable('form_view_edit_row'),
} satisfies { [K in FieldTypeName]: FieldBehavior<K> }

/** The behaviour of one field, by its type. */
export function behaviorOf<T extends FieldTypeName>(type: T): FieldBehavior<T> {
  return FIELD_BEHAVIOR[type] as unknown as FieldBehavior<T>
}

/** Whether a view can be ordered by a field at all. */
export function canSortInView(field: Field): boolean {
  return behaviorOf(field.type).getSort(field, 'field_0', 'ASC') !== null
}

/**
 * Whether a view can be *grouped* by a field.
 *
 * A stricter question than whether it can be ordered by one, and not a question
 * the ordering behaviour can answer: a file column sorts by its filenames and a
 * uuid sorts perfectly well, and neither is anything anybody would want a
 * heading for. The backend keeps the two apart the same way, as `_can_group_by`
 * beside `check_can_order_by`, and this map is that flag.
 *
 * Exhaustive, so a twenty-eighth field type has to say which it is rather than
 * arriving as a column silently missing from the menu -- which reads as the
 * field being broken rather than as grouping not applying to it.
 */
const CAN_GROUP_BY = {
  autonumber: false,
  boolean: true,
  count: false,
  created_by: false,
  created_on: true,
  date: true,
  duration: true,
  email: true,
  file: false,
  form_view_edit_row: false,
  formula: false,
  last_modified: true,
  last_modified_by: false,
  link_row: false,
  long_text: false,
  lookup: false,
  multiple_collaborators: true,
  multiple_select: true,
  number: true,
  password: false,
  phone_number: true,
  rating: true,
  rollup: false,
  single_select: true,
  text: true,
  url: true,
  uuid: false,
} as const satisfies Record<FieldTypeName, boolean>

/**
 * The formula result types a view can be grouped by.
 *
 * Not the same answer as the field type each behaves like, which is why it is a
 * table of its own: a `single_select` *field* can be grouped by and a formula
 * returning one cannot, because the backend groups on the stored column and a
 * formula's select is computed. The scalar types are groupable and everything
 * with a shape -- an array, a file, a link, a button -- is not.
 */
const FORMULA_CAN_GROUP_BY = {
  invalid: false,
  text: true,
  char: true,
  button: false,
  link: false,
  date_interval: true,
  duration: true,
  date: true,
  boolean: true,
  number: true,
  array: false,
  single_select: false,
  multiple_select: false,
  single_file: false,
  url: true,
  multiple_collaborators: false,
} as const satisfies Record<FormulaType, boolean>

export function canGroupByInView(field: Field): boolean {
  switch (field.type) {
    // A long text column can be grouped by unless it holds rich text, which is
    // markup rather than a value and has no single thing to head a group with.
    case 'long_text':
      return field.long_text_enable_rich_text !== true
    // A link row is grouped by whatever the *related* table's primary field
    // would be grouped by, because that is what the cell holds.
    case 'link_row': {
      // The same cast `sortLinkRow` makes, and for the same reason: the
      // endpoint serialises the whole related field, `type` included, but the
      // schema describes it as a union with the discriminator left out.
      const related = field.link_row_table_primary_field as LinkRowPrimaryField
      return related !== null && related !== undefined && canGroupByInView(related)
    }
    case 'formula':
    case 'count':
    case 'rollup':
    case 'lookup': {
      const formulaType = field.formula_type
      return (
        formulaType !== undefined &&
        formulaType !== null &&
        formulaType in FORMULA_CAN_GROUP_BY &&
        FORMULA_CAN_GROUP_BY[formulaType]
      )
    }
    // The map alone. The backend asks `_can_group_by and check_can_order_by`,
    // and the second half is redundant here: every type this map answers yes
    // to has an order, and the three that have none -- a file, a password, a
    // form marker -- are all no. Written in, it is a condition no test can
    // tell from its absence.
    default:
      return CAN_GROUP_BY[field.type]
  }
}

// ---------------------------------------------------------------------------
// The two types whose order is defined by another field
// ---------------------------------------------------------------------------

/**
 * Orders rows by a link row cell.
 *
 * The cell holds the related table's primary field rendered as text, so the
 * comparison is that field's, applied entry by entry. Three tie-breaks follow,
 * in the backend's order: an empty cell first, then a shorter list before a
 * longer one that starts the same way, then the linked rows' own order and id --
 * without which two cells naming the same rows in different orders would compare
 * as different, and the view would reshuffle on every refetch.
 */
function sortLinkRow(
  field: FieldOf<'link_row'>,
  key: FieldKey,
  direction: SortDirection,
  context?: FieldContext,
): RowComparator | null {
  const related = field.link_row_table_primary_field as LinkRowPrimaryField
  if (related === null || related === undefined) return null

  const relatedBehavior = behaviorOf(related.type)
  const compareOne = relatedBehavior.getSort(related, key, direction, context)
  if (compareOne === null) return null

  const parse = (item: LinkedRowValue): unknown =>
    relatedBehavior.parseFromLinkedRowItemValue(related, item.value ?? '')

  return (rowA, rowB) => {
    const itemsA = (cell(rowA, key) ?? []) as LinkedRowValue[]
    const itemsB = (cell(rowB, key) ?? []) as LinkedRowValue[]
    const valuesA = itemsA.map(parse)
    const valuesB = itemsB.map(parse)

    // An empty cell comes first whichever way the view is ordered, matching the
    // backend, which puts an empty array before every non-empty one.
    if (valuesA.length === 0 && valuesB.length !== 0) return -1
    if (valuesA.length !== 0 && valuesB.length === 0) return 1

    for (let i = 0; i < Math.max(valuesA.length, valuesB.length); i += 1) {
      const hasA = i < valuesA.length
      const hasB = i < valuesB.length

      let compared = 0
      if (hasA && hasB) {
        const a = valuesA[i]
        const b = valuesB[i]
        if (a !== null && b !== null) {
          compared = compareOne(
            { [key]: a } as unknown as RowData,
            {
              [key]: b,
            } as unknown as RowData,
          )
        } else if (a !== null) {
          // Postgres puts nulls last *within* an array, the opposite way round
          // from how it treats a null column.
          compared = direction === 'ASC' ? -1 : 1
        } else if (b !== null) {
          compared = direction === 'ASC' ? 1 : -1
        }
      } else if (hasA) {
        compared = direction === 'ASC' ? 1 : -1
      } else if (hasB) {
        compared = direction === 'ASC' ? -1 : 1
      }

      if (compared !== 0) return compared
    }

    for (let i = 0; i < itemsA.length; i += 1) {
      const orderA = new BigNumber((itemsA[i] as LinkedRowValue).order ?? 0)
      const orderB = new BigNumber((itemsB[i] as LinkedRowValue).order ?? 0)
      if (!orderA.isEqualTo(orderB)) {
        const result = orderA.isLessThan(orderB) ? -1 : 1
        return direction === 'ASC' ? result : -result
      }
    }

    for (let i = 0; i < itemsA.length; i += 1) {
      const idA = (itemsA[i] as LinkedRowValue).id
      const idB = (itemsB[i] as LinkedRowValue).id
      if (idA !== idB) {
        return direction === 'ASC' ? idA - idB : idB - idA
      }
    }

    // The same rows in the same order. Whatever sort comes next decides.
    return 0
  }
}

type FormulaField =
  FieldOf<'formula'> | FieldOf<'count'> | FieldOf<'rollup'> | FieldOf<'lookup'>

/**
 * Orders rows by a formula, rollup, count or lookup cell.
 *
 * A formula has no type of its own, so this is whichever ordinary field type the
 * result behaves like. An array result is compared entry by entry using the
 * entry type's own comparison.
 */
/**
 * The behaviour a formula field answers as.
 *
 * A formula has no filtering behaviour of its own -- it borrows that of the type
 * its result behaves like, so a formula returning text is substring-matchable
 * and one returning a duration is not. Null when the result type has no
 * equivalent field type, in which case the filter simply does not apply.
 */
function formulaDelegate(field: FormulaField): FieldBehavior | null {
  const formulaType = field.formula_type
  if (!isArrayFormulaType(formulaType)) return null
  return behaviorOf(FORMULA_TYPE_BEHAVES_AS[formulaType])
}

/**
 * Renders the formula result types whose text is not their field type's.
 *
 * Three formula types produce a value shape no field type has. A `url`, `link`
 * or `button` result can be either a bare string or a `{ label, url }` pair,
 * which reads as "label (url)"; a `single_file` result is one file object
 * rather than the list a file column holds. Falling through to the field
 * behaviour would render each of these as `[object Object]`, and that text is
 * what a `contains` filter matches against.
 */
const FORMULA_RENDER_OVERRIDES: Record<string, (value: unknown) => string> = {
  url: renderLinkLike,
  link: renderLinkLike,
  button: renderLinkLike,
  single_file: (value) => {
    const name = (value as { visible_name?: unknown } | null)?.visible_name
    return typeof name === 'string' ? name : ''
  },
}

function renderLinkLike(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  const { label, url } = value as { label?: unknown; url?: unknown }
  if (typeof label === 'string' && label !== '') {
    return `${label} (${typeof url === 'string' ? url : ''})`
  }
  // Upstream returns `undefined` when there is no url either, which its callers
  // then read as "no text". The empty string means the same to all of them.
  return typeof url === 'string' ? url : ''
}

/**
 * Renders a formula cell as the type its result behaves like.
 *
 * A formula has no rendering of its own: a formula returning a date must read
 * as a date, in the field's own date format, or a `contains` filter over it
 * matches text nobody sees. An array result renders each entry through the
 * entry type and joins them, which is what a lookup column shows.
 */
function formulaToHumanReadable(
  field: FormulaField,
  value: unknown,
  context?: FieldContext,
  delimiter = ', ',
): string {
  const formulaType = field.formula_type

  if (formulaType === 'array') {
    const entryType = field.array_formula_type
    if (!isArrayFormulaType(entryType)) return ''

    const renderEntry =
      FORMULA_RENDER_OVERRIDES[entryType] ??
      ((item: unknown) =>
        behaviorOf(FORMULA_TYPE_BEHAVES_AS[entryType]).toHumanReadable(
          field,
          item,
          context,
          delimiter,
        ))
    const entries = Array.isArray(value) ? value : []
    return entries
      .map((item) => renderEntry(unwrapArrayItem(entryType, item)))
      .join(delimiter)
  }

  const override = FORMULA_RENDER_OVERRIDES[formulaType ?? '']
  if (override !== undefined) return override(value)

  if (!isArrayFormulaType(formulaType)) return toHumanReadableByDefault(value)

  return behaviorOf(FORMULA_TYPE_BEHAVES_AS[formulaType]).toHumanReadable(
    field,
    value,
    context,
    delimiter,
  )
}

function sortFormula(
  field: FormulaField,
  key: FieldKey,
  direction: SortDirection,
  context?: FieldContext,
): RowComparator | null {
  const formulaType = field.formula_type

  if (formulaType === 'array') {
    return sortArrayFormula(field, key, direction, context)
  }
  if (!isArrayFormulaType(formulaType)) return null

  const behavior = behaviorOf(FORMULA_TYPE_BEHAVES_AS[formulaType])
  return behavior.getSort(field, key, direction, context)
}

function sortArrayFormula(
  field: FormulaField,
  key: FieldKey,
  direction: SortDirection,
  context?: FieldContext,
): RowComparator | null {
  const entryType = field.array_formula_type
  if (!isArrayFormulaType(entryType)) return null

  const behavior = behaviorOf(FORMULA_TYPE_BEHAVES_AS[entryType])
  const compareOne = behavior.getSort(field, key, direction, context)
  if (compareOne === null) return null

  const unwrap = (item: unknown): unknown => unwrapArrayItem(entryType, item)

  return (rowA, rowB) => {
    const rawA = cell(rowA, key)
    const rawB = cell(rowB, key)
    const valuesA = (Array.isArray(rawA) ? rawA : [rawA]).map(unwrap)
    const valuesB = (Array.isArray(rawB) ? rawB : [rawB]).map(unwrap)

    for (let i = 0; i < Math.max(valuesA.length, valuesB.length); i += 1) {
      const a = valuesA[i]
      const b = valuesB[i]

      // `false` and `''` are values, not absences, so the emptiness test cannot
      // be a truthiness test -- an unticked checkbox in a lookup would otherwise
      // sort as though the row were not there.
      const filledA = i < valuesA.length && a !== null && a !== undefined && a !== false
      const filledB = i < valuesB.length && b !== null && b !== undefined && b !== false

      let compared = 0
      if (filledA && filledB) {
        compared = compareOne(
          { [key]: a } as unknown as RowData,
          {
            [key]: b,
          } as unknown as RowData,
        )
      } else if (filledA !== filledB) {
        // A filled entry beats an unfilled one; among unfilled ones a null beats
        // a missing entry, which is Postgres' order for array elements.
        const aWins = filledA ? 1 : -1
        compared = direction === 'ASC' ? aWins : -aWins
      } else if (a === null && b === undefined) {
        compared = direction === 'ASC' ? 1 : -1
      } else if (a === undefined && b === null) {
        compared = direction === 'ASC' ? -1 : 1
      }

      if (compared !== 0) return compared
    }

    // Upstream falls off the end of this loop and returns undefined, which
    // `Array.prototype.sort` reads as an unspecified ordering.
    return 0
  }
}

function parseFormulaLinkedValue(field: FormulaField, value: string): unknown {
  const formulaType = field.formula_type
  if (!isArrayFormulaType(formulaType)) return value
  const behavior = behaviorOf(FORMULA_TYPE_BEHAVES_AS[formulaType])
  return behavior.parseFromLinkedRowItemValue(field, value)
}
